import React from "react";
import {
    Box,
    Chip,
    FormControl,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
} from "@mui/material";

import {
    PersonSearch as PersonSearchIcon,
} from "@mui/icons-material";

import AssessmentTabEmptyState from "./AssessmentTabEmptyState";
import BatchDOKSection from "./BatchDOKSection";

import {
    getProficiencySelectSx,
    NEUTRAL_STATUS_COLORS,
} from "./batchAssessmentColors";

import { getComputedAssessmentStatus } from "./batchAssessmentStatus";

const OVERALL_ASSESSMENT_OPTIONS = [
    "Emerging",
    "Developing",
    "Proficient",
    "Extending",
];

const BatchStudentDetail = ({
    student,
    studentState,
    skills = [],
    dokLevels = [],
    onSkillChange,
    onProficiencyChange,
    onOverallAssessmentChange,
    onCommentChange,
}) => {
    if (!student || !studentState) {
        return (
            <AssessmentTabEmptyState
                icon={PersonSearchIcon}
                title="Select a student to get started"
                description="Choose a student from the list on the left to view or create their assessment."
            />
        );
    }

    // Assessed means at least one Skill is currently achieved.
    // Not Assessed means none are — this is computed automatically
    // from the Skill entries below, matching the backend, and is
    // no longer a manual teacher toggle.
    const assessmentStatus = getComputedAssessmentStatus(
        studentState.entries
    );

    const isNotAssessed =
        assessmentStatus === "not_assessed";

    return (
        <Paper
            elevation={0}
            sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 3,
                overflow: "hidden",
            }}
        >
            {/* =================================================
                Student Header
            ================================================= */}

            <Box
                sx={{
                    px: {
                        xs: 2,
                        sm: 3,
                    },
                    py: 2,
                    backgroundColor: "#F7F7F2",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                }}
            >
                <Stack
                    direction={{
                        xs: "column",
                        sm: "row",
                    }}
                    justifyContent="space-between"
                    alignItems={{
                        xs: "flex-start",
                        sm: "center",
                    }}
                    spacing={1}
                >
                    <Box>
                        <Typography
                            variant="h6"
                            fontWeight={700}
                        >
                            {student.student_full_name}
                        </Typography>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            Class {student.class_level}
                            {student.section_level
                                ? ` • Section ${student.section_level}`
                                : ""}
                            {student.academic_year
                                ? ` • ${student.academic_year}`
                                : ""}
                        </Typography>
                    </Box>

                    <Chip
                        size="small"
                        label={
                            studentState.exists
                                ? "Previously Assessed"
                                : "New Assessment"
                        }
                        sx={
                            studentState.exists
                                ? {
                                      fontWeight: 700,
                                      color: "#425126",
                                      backgroundColor:
                                          "#DCE6C5",
                                  }
                                : {
                                      fontWeight: 700,
                                      color: NEUTRAL_STATUS_COLORS
                                          .not_assessed.text,
                                      backgroundColor: "transparent",
                                      border: "1px solid",
                                      borderColor:
                                          NEUTRAL_STATUS_COLORS
                                              .not_assessed.border,
                                  }
                        }
                    />
                </Stack>
            </Box>

            {/* =================================================
                Assessment Content
            ================================================= */}

            <Stack
                spacing={2.5}
                sx={{
                    p: {
                        xs: 2,
                        sm: 3,
                    },
                }}
            >
                {/* ---------------------------------------------
                    Assessment Status
                --------------------------------------------- */}

                <Box>
                    <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        sx={{
                            mb: 0.75,
                        }}
                    >
                        Status
                    </Typography>

                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                    >
                        <Chip
                            size="small"
                            label={
                                isNotAssessed
                                    ? "Not Assessed"
                                    : "Assessed"
                            }
                            sx={{
                                fontWeight: 700,
                                color: isNotAssessed
                                    ? NEUTRAL_STATUS_COLORS
                                          .not_assessed.text
                                    : "#2E6B2E",
                                backgroundColor: isNotAssessed
                                    ? NEUTRAL_STATUS_COLORS
                                          .not_assessed.bg
                                    : "#E8F3E1",
                            }}
                        />

                        <Typography
                            variant="caption"
                            color="text.secondary"
                        >
                            Set automatically once at least
                            one skill is checked below.
                        </Typography>
                    </Stack>
                </Box>

                {/* ---------------------------------------------
                    DOK Sections
                --------------------------------------------- */}

                {dokLevels.map((dokLevel) => (
                    <BatchDOKSection
                        key={dokLevel.id}
                        dokLevel={dokLevel}
                        skills={skills}
                        studentState={studentState}
                        onSkillChange={
                            onSkillChange
                        }
                        onProficiencyChange={
                            onProficiencyChange
                        }
                    />
                ))}

                {/* ---------------------------------------------
                    Overall Assessment
                --------------------------------------------- */}

                <Box>
                    <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        sx={{
                            mb: 0.75,
                        }}
                    >
                        Overall Proficiency
                    </Typography>

                    <FormControl
                        fullWidth
                        size="small"
                    >
                        <Select
                            value={
                                studentState.overallAssessment ||
                                ""
                            }
                            displayEmpty
                            onChange={(event) =>
                                onOverallAssessmentChange(
                                    event.target.value
                                )
                            }
                            sx={getProficiencySelectSx(
                                studentState.overallAssessment ||
                                    ""
                            )}
                        >
                            <MenuItem value="">
                                <em>
                                    Select Overall Proficiency
                                </em>
                            </MenuItem>

                            {OVERALL_ASSESSMENT_OPTIONS.map(
                                (option) => (
                                    <MenuItem
                                        key={option}
                                        value={option}
                                    >
                                        {option}
                                    </MenuItem>
                                )
                            )}
                        </Select>
                    </FormControl>
                </Box>

                {/* ---------------------------------------------
                    Educator Comment
                --------------------------------------------- */}

                <Box>
                    <Typography
                        variant="subtitle2"
                        fontWeight={700}
                        sx={{
                            mb: 0.75,
                        }}
                    >
                        Teacher's Note
                    </Typography>

                    <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        value={
                            studentState.educatorComment ||
                            ""
                        }
                        placeholder="Add an optional note about this student's learning..."
                        onChange={(event) =>
                            onCommentChange(
                                event.target.value
                            )
                        }
                    />
                </Box>
            </Stack>
        </Paper>
    );
};

export default BatchStudentDetail;